import React from 'react'
import Cookies from 'js-cookie'
import { Form, Field } from 'react-final-form'
import { Button, PasswordInput, InputField, Separator } from '@mc/design-system'
import { Trans, useTranslation } from '@mc/i18n'
import { paths } from '@mc/paths'
import { trackPasswordVisibilityToggled } from '@mc/pre-paywall-events'
import {
  track,
  Actions,
  Locations,
  PageSource,
  EventTypes,
} from '@mc/track-event'
import AppleButton from '../../../components/AppleButton'
import { EmailFormatWarning } from '../../../components/EmailFormatWarning'
import FacebookButton from '../../../components/FacebookButton'
// Experiment imports
import GoogleButton from '../../../components/GoogleButton'
import { LOGIN_TITLE } from '../../../constants'
import { isGiftAccountClarificationOn } from '../../../utils'
import { validateLogin } from '../../../validations'
import { LAST_LOGIN_REMINDER_NAMESPACE } from '../i18n'
import { getLastLoginMethod } from '../utils/lastLoginMethod'
import { WithLastLoginBadge } from './LastLoginBadge'
import { LoginWithEmailToggle } from './LoginWithEmailToggle'
import { Terms } from './Terms'

type LoginFormProps = {
  onSubmit: (values: unknown) => void | Promise<unknown>
  onForgotPasswordClick: () => void
  onRegisterClick: () => void
  facebookLogin: () => void
  appleLogin: (data: unknown) => void
  googleLogin?: () => void
  nextPage?: string
  thirdPartyError?: string
  userAgent?: string
  onEmailToggleExpand?: () => void
}

const LoginForm = ({
  onSubmit,
  onForgotPasswordClick,
  onRegisterClick,
  facebookLogin,
  appleLogin,
  googleLogin,
  thirdPartyError,
  nextPage,
  userAgent,
  onEmailToggleExpand,
}: LoginFormProps) => {
  const { t } = useTranslation('@mc/authentication')
  const { t: tExperiment } = useTranslation(LAST_LOGIN_REMINDER_NAMESPACE)
  const lastLoginMethod = getLastLoginMethod()

  const onClickSignUp = () => {
    track(EventTypes.LOG_IN_MODAL_CTA_CLICKED, {
      action: Actions.SIGN_UP_CTA,
      location: Locations.LOG_IN_MODAL,
      href: paths.root,
      page_source: PageSource.LOG_IN,
    })

    onRegisterClick()
  }

  const handleForgotPassword = () => {
    onForgotPasswordClick()
  }

  const handleGoogleLogin = () => {
    googleLogin?.()
  }

  const handleFacebookLogin = () => {
    facebookLogin()
  }

  const handleAppleLogin = (data: unknown) => {
    ;(appleLogin as (data: unknown) => void)(data)
  }

  const initialValues = {
    email: Cookies.get('cart_email')?.replace(/['"]+/g, ''),
  }

  const isAppleTv = nextPage === '/appletv'

  return (
    <div>
      <h4 className='mc-text-h4 mc-text--center mc-mb-6'>{LOGIN_TITLE}</h4>

      {thirdPartyError && (
        <p className='mc-text--error mc-text--center mc-mb-4'>
          {thirdPartyError}
        </p>
      )}

      <div className='mc-mb-4'>
        <WithLastLoginBadge isLastLoginMethod={lastLoginMethod === 'google'}>
          <GoogleButton userAgent={userAgent} onClick={handleGoogleLogin}>
            {t('loginForm.google')}
          </GoogleButton>
        </WithLastLoginBadge>
      </div>

      <div className='mc-mb-4' data-ba='facebook-login'>
        <WithLastLoginBadge isLastLoginMethod={lastLoginMethod === 'facebook'}>
          <FacebookButton onLogin={handleFacebookLogin} userAgent={userAgent}>
            {t('loginForm.facebook')}
          </FacebookButton>
        </WithLastLoginBadge>
      </div>

      <div className='mc-mb-4'>
        <WithLastLoginBadge isLastLoginMethod={lastLoginMethod === 'apple'}>
          <AppleButton onLogin={handleAppleLogin} />
        </WithLastLoginBadge>
      </div>

      <div className='mc-mb-4'>
        <p className='mc-text-x-small mc-text-hr mc-opacity--muted mc-text--uppercase mc-mt-3'>
          <span>{t('loginForm.or')}</span>
        </p>
      </div>

      <LoginWithEmailToggle
        isLastLoginMethod={lastLoginMethod === 'email'}
        onExpand={() => {
          // Tracking is now handled by container
          onEmailToggleExpand?.()
        }}
      >
        <Form
          onSubmit={onSubmit}
          initialValues={initialValues}
          validate={validateLogin}
          render={({ handleSubmit, submitting, submitError }) => (
            <form name='login-form' onSubmit={handleSubmit}>
              {!submitting && submitError && (
                <p
                  aria-live='assertive'
                  className='mc-text--error mc-text--center mc-mb-4'
                >
                  {submitError}
                </p>
              )}

              <Field
                component={InputField}
                className='mc-mb-4'
                id='email'
                name='email'
                type='email'
                label='Email'
                data-ba='email-input'
                data-testid='email-input'
                autoComplete='email'
                required
              />

              <EmailFormatWarning
                pageSource={PageSource.LOG_IN}
                trackProps={{
                  location: 'login_modal',
                }}
              />

              <Field
                component={PasswordInput}
                className='mc-mb-4'
                id='password'
                name='password'
                type='password'
                label='Password'
                data-ba='password-input'
                data-testid='password-input'
                autoComplete='current-password'
                required
                onTogglePassword={(visible: boolean) => {
                  trackPasswordVisibilityToggled({
                    visible,
                    location: Locations.LOG_IN_MODAL,
                    pageSource: PageSource.LOG_IN,
                  })
                }}
              />

              <Button
                type='submit'
                loading={submitting}
                fullWidth
                data-ba='submit-btn'
                data-testid='submit-btn'
              >
                {t('loginForm.logIn')}
              </Button>

              {isGiftAccountClarificationOn() ? (
                <Terms i18nKey='giftLoginTerms' className='mc-mx-6' />
              ) : (
                <Terms />
              )}
            </form>
          )}
        />
      </LoginWithEmailToggle>

      <p className='mc-text--center mc-my-4'>
        <a
          href={paths.ssoLogIn(nextPage)}
          data-ba='sso-sign-in-button'
          target='_blank'
          rel='noreferrer'
          className='mc-text--link mc-text--nowrap mc-text-small'
        >
          {t('loginForm.organizationLogin')}
        </a>
      </p>

      {!isAppleTv && (
        <>
          {isGiftAccountClarificationOn() ? (
            <>
              <ForgotPasswordLink onClick={handleForgotPassword} />
              <Separator />
              <p className='mc-text--center mc-mt-4 mc-mx-8'>
                <Trans i18nKey='needAccount' ns='@mc/authentication'>
                  <button
                    className='mc-text--link'
                    onClick={onRegisterClick}
                  ></button>
                </Trans>
              </p>
            </>
          ) : (
            <p className='mc-text--center'>
              <button
                onClick={() => onClickSignUp()}
                className='mc-text--link mc-text--nowrap mc-text-small'
              >
                {tExperiment('signUp')}
              </button>
              <span style={{ margin: '0 4px' }}>{'・'}</span>
              <button
                onClick={handleForgotPassword}
                className='mc-text--link mc-text--nowrap mc-text-small'
              >
                {tExperiment('resetPassword')}
              </button>
            </p>
          )}
        </>
      )}
    </div>
  )
}

const ForgotPasswordLink = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslation('@mc/authentication')
  return (
    <p className='mc-text-x-small mc-text--center mc-opacity--muted mc-text--link mc-mt-4'>
      <button className='mc-text--underline' onClick={onClick}>
        {t('forgotPassword')}
      </button>
    </p>
  )
}

export default LoginForm
